import dayjs, { Dayjs } from "@/lib/dayjs";

// 타입 정의
interface Translations {
  justNow: string;
  minutes: string;
  hours: string;
  days: string;
  months: string;
  years: string;
}

export const formatRelativeTime = (date: string | Date | Dayjs | null, lang: string = "en"): string => {
  if (!date) {
    return "";
  }

  // 브라우저에서 클라이언트의 타임존 가져오기 (옵셔널)
  let clientTimeZone: string | undefined;
  try {
    clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    console.warn("Failed to get client timezone, falling back to local timezone", e);
  }

  // 현재 시간 설정: clientTimeZone가 있으면 적용, 없으면 로컬 타임존 사용
  const now = clientTimeZone ? dayjs().tz(clientTimeZone) : dayjs().local();

  let past: Dayjs;

  // 입력된 date를 UTC로 파싱한 후 타임존 적용
  if (typeof date === "string") {
    past = clientTimeZone ? dayjs.utc(date).tz(clientTimeZone) : dayjs.utc(date).local();
  } else if (date instanceof Date) {
    past = clientTimeZone ? dayjs.utc(date).tz(clientTimeZone) : dayjs.utc(date).local();
  } else {
    // Dayjs 객체일 경우 UTC로 간주하고 타임존 적용
    past = clientTimeZone ? date.utc().tz(clientTimeZone) : date.utc().local();
  }

  // 다국어 번역 객체 (변경 없음)
  const translations: Record<string, Translations> = {
    en: {
      justNow: "Just now",
      minutes: "minutes ago",
      hours: "hours ago",
      days: "days ago",
      months: "months ago",
      years: "years ago",
    },
    de: {
      justNow: "Gerade eben",
      minutes: "Minuten zuvor",
      hours: "Stunden zuvor",
      days: "Tage zuvor",
      months: "Monate zuvor",
      years: "Jahre zuvor",
    },
    ko: {
      justNow: "방금 전",
      minutes: "분 전",
      hours: "시간 전",
      days: "일 전",
      months: "개월 전",
      years: "년 전",
    },
    ja: {
      justNow: "たった今",
      minutes: "分前",
      hours: "時間前",
      days: "日前",
      months: "ヶ月前",
      years: "年前",
    },
    "zh-cn": {
      justNow: "刚刚",
      minutes: "分钟前",
      hours: "小时前",
      days: "天前",
      months: "个月前",
      years: "年前",
    },
    "zh-tw": {
      justNow: "剛剛",
      minutes: "分鐘前",
      hours: "小時前",
      days: "天前",
      months: "個月前",
      years: "年前",
    },
    "zh-hk": {
      justNow: "啱啱",
      minutes: "分鐘前",
      hours: "小時前",
      days: "日前",
      months: "個月前",
      years: "年前",
    },
    es: {
      justNow: "Ahora mismo",
      minutes: "minutos atrás",
      hours: "horas atrás",
      days: "días atrás",
      months: "meses atrás",
      years: "años atrás",
    },
    fr: {
      justNow: "À l’instant",
      minutes: "minutes auparavant",
      hours: "heures auparavant",
      days: "jours auparavant",
      months: "mois auparavant",
      years: "ans auparavant",
    },
    pt: {
      justNow: "Agora mesmo",
      minutes: "minutos atrás",
      hours: "horas atrás",
      days: "dias atrás",
      months: "meses atrás",
      years: "anos atrás",
    },
    ru: {
      justNow: "Только что",
      minutes: "минут назад",
      hours: "часов назад",
      days: "дней назад",
      months: "месяцев назад",
      years: "лет назад",
    },
    ar: {
      justNow: "الآن",
      minutes: "دقائق مضت",
      hours: "ساعات مضت",
      days: "أيام مضت",
      months: "شهور مضت",
      years: "سنوات مضت",
    },
    hi: {
      justNow: "अभी",
      minutes: "मिनट पहले",
      hours: "घंटे पहले",
      days: "दिन पहले",
      months: "महीने पहले",
      years: "साल पहले",
    },
    vi: {
      justNow: "Vừa xong",
      minutes: "phút trước",
      hours: "giờ trước",
      days: "ngày trước",
      months: "tháng trước",
      years: "năm trước",
    },
    th: {
      justNow: "刚刚",
      minutes: "นาทีที่แล้ว",
      hours: "ชั่วโมงที่แล้ว",
      days: "วันที่แล้ว",
      months: "เดือนที่แล้ว",
      years: "ปีที่แล้ว",
    },
    id: {
      justNow: "Baru saja",
      minutes: "menit yang lalu",
      hours: "jam yang lalu",
      days: "hari yang lalu",
      months: "bulan yang lalu",
      years: "tahun yang lalu",
    },
  };

  const t = translations[lang] || translations["en"]; // 기본값은 영어

  const months = now.diff(past, "month");
  const years = now.diff(past, "year");

  if (months < 1) {
    const days = now.diff(past, "day");

    if (days < 1) {
      const hours = now.diff(past, "hour");

      if (hours < 1) {
        const minutes = now.diff(past, "minute");
        return minutes < 1 ? t.justNow : `${minutes}${t.minutes}`;
      }

      return `${hours}${t.hours}`;
    }

    return `${days}${t.days}`;
  } else if (years < 1) {
    return `${months}${t.months}`;
  } else {
    return `${years}${t.years}`;
  }
};
// 타입 정의
interface ViewSuffix {
  views: string; // "회", "views" 등 조회수 접미사
}

export function formatViewCount(views: number, lang: string = "en"): string {
  if (views === null || views === undefined) {
    return "";
  }
  // 다국어 접미사 객체
  const suffixes: Record<string, ViewSuffix> = {
    en: { views: "views" },
    de: { views: "Aufrufe" },
    ko: { views: "회" },
    ja: { views: "回" },
    "zh-cn": { views: "次" },
    "zh-tw": { views: "次" },
    "zh-hk": { views: "次" },
    es: { views: "visualizaciones" },
    fr: { views: "vues" },
    pt: { views: "visualizações" },
    ru: { views: "просмотров" },
    ar: { views: "مشاهدة" },
    hi: { views: "बार" },
    vi: { views: "lượt xem" },
    th: { views: "ครั้ง" },
    id: { views: "x ditonton" },
  };

  const t = suffixes[lang] || suffixes["en"]; // 기본값은 영어

  // Intl.NumberFormat으로 숫자 포맷팅

  return `${formatCount(views, lang)} ${t.views}`;
}

export function formatCount(views: number, lang: string = "en"): string {
  if (views === null || views === undefined) {
    return "";
  }
  // Intl.NumberFormat으로 숫자 포맷팅
  const formatter = new Intl.NumberFormat(lang, {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: views < 10_000_000 ? 1 : 0, // 1000만 미만은 소수점 1자리, 이상은 정수
  });

  const formattedNumber = formatter.format(views);
  return `${formattedNumber}`;
}

export const formatNumber = (value: number, locale?: string, fractionDigits: number = 0): string => {
  const finalLocale = locale || navigator.language || "en-US";
  return new Intl.NumberFormat(finalLocale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);
};

const dateFormatMap: Record<string, string> = {
  en: "MM/DD/YYYY", // 미국, 캐나다
  de: "DD.MM.YYYY", // 독일, 오스트리아, 스위스
  ko: "YYYY.MM.DD", // 한국
  ja: "YYYY/MM/DD", // 일본
  "zh-cn": "YYYY-MM-DD", // 중국 (간체)
  "zh-tw": "YYYY/MM/DD", // 대만 (번체)
  "zh-hk": "YYYY/MM/DD", // 홍콩
  es: "DD/MM/YYYY", // 스페인, 남미
  fr: "DD/MM/YYYY", // 프랑스, 벨기에, 스위스
  pt: "DD/MM/YYYY", // 포르투갈, 브라질
  ru: "DD.MM.YYYY", // 러시아
  ar: "DD/MM/YYYY", // 아랍권
  hi: "DD/MM/YYYY", // 인도, 네팔
  vi: "DD/MM/YYYY", // 베트남
  th: "DD/MM/YYYY", // 태국
  id: "DD/MM/YYYY", // 인도네시아
};

export const getDateFormat = (locale: string) => dateFormatMap[locale] || "YYYY-MM-DD"; // 기본값 설정

export const backgroundPickColors = [
  "#000000", // 검정
  "#FFFFFF", // 흰색
  "#323232", // 어두운 회색
  "#F5D7AF", // 연한 베이지
  "#1A3C5A", // 다크 블루
  "#E0E0E0", // 연한 회색
  "#1C2526", // 반투명 블랙
  "#D4E4D8", // 연한 민트
  "#3F2A44", // 다크 퍼플
  "#F9E4E4", // 연한 핑크
  "#2F4F4F", // 다크 그린
];

export const textPickColors = [
  "#000000", // 검정
  "#FFFFFF", // 흰색
  "#FFF5CC", // 밝은 크림색
  "#333333", // 진한 회색
  "#4A2C2A", // 진한 갈색
  "#B0C4DE", // 밝은 그레이 블루
  "#F8F1E9", // 부드러운 흰색
  "#FFD700", // 연한 골드
  "#355E3B", // 다크 그린
  "#E6E6FA", // 연한 라벤더
  "#8B0000", // 다크 레드
  "#D4F4A2", // 연한 라임
];

export const rankingColors = [
  " #FFD700, #FFC107, #FFB300",
  " #D3D3D3, #C0C0C0, #A9A9A9",
  " #CD7F32, #D2691E, #A0522D",
  "#78909C",
];
